import React, { useState, useEffect } from 'react';
import { Settings, Server, Key, Save, Mail, AlertTriangle, CheckCircle } from 'lucide-react';
import { fetchWithAuth } from '../../utils/api'; // Assuming you have this helper
import './EmailConfiguration.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const EmailConfiguration = () => {
  const [settings, setSettings] = useState({
    host: '',
    port: '',
    user: '',
    pass: '', // Password will only be sent, not displayed
    senderName: '',
    senderEmail: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await fetchWithAuth(`${API_BASE_URL}/api/email/settings`);
        // We don't set 'pass' for security, it remains blank in the form
        setSettings(prev => ({ ...prev, ...data }));
      } catch (err) {
        setMessage({ type: 'error', text: `Failed to load settings: ${err.message}` });
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const result = await fetchWithAuth(`${API_BASE_URL}/api/email/settings`, {
        method: 'POST',
        body: JSON.stringify(settings),
      });
      setMessage({ type: 'success', text: result.message });
    } catch (err) {
      setMessage({ type: 'error', text: `Failed to save: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setMessage({ type: '', text: '' });
    try {
      const result = await fetchWithAuth(`${API_BASE_URL}/api/email/test-connection`, {
        method: 'POST',
        body: JSON.stringify(settings),
      });
      setMessage({ type: 'success', text: result.message });
    } catch (err) {
      setMessage({ type: 'error', text: `Connection Failed: ${err.message}` });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div>Loading configuration...</div>;
  }

  return (
    <div className="email-config-page">
      <div className="email-config-header">
        <h1>
          <Settings size={28} /> Email Service Configuration
        </h1>
        <p>Configure your SMTP settings to enable sending emails from the platform.</p>
      </div>

      {message.text && (
        <div className={`message-banner ${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          {message.text}
        </div>
      )}

      <form className="email-config-form" onSubmit={handleSaveSettings}>
        <div className="form-section">
          <h3><Server size={20} /> SMTP Server Settings</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>SMTP Host</label>
              <input name="host" value={settings.host} onChange={handleInputChange} placeholder="e.g., smtp.gmail.com" required />
            </div>
            <div className="form-field">
              <label>Port</label>
              <input name="port" type="number" value={settings.port} onChange={handleInputChange} placeholder="e.g., 587 or 465" required />
            </div>
            <div className="form-field">
              <label>Username / Email</label>
              <input name="user" type="email" value={settings.user} onChange={handleInputChange} placeholder="Your SMTP username" required />
            </div>
            <div className="form-field">
              <label>Password / App Password</label>
              <input name="pass" type="password" value={settings.pass} onChange={handleInputChange} placeholder="Enter new password to update" />
              <small>Leave blank to keep the existing password.</small>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3><Mail size={20} /> Sender Details</h3>
          <div className="form-grid">
            <div className="form-field">
                <label>Sender Name</label>
                <input name="senderName" value={settings.senderName} onChange={handleInputChange} placeholder="e.g., Immigration Admin" required />
            </div>
            <div className="form-field">
                <label>Sender Email</label>
                <input name="senderEmail" type="email" value={settings.senderEmail} onChange={handleInputChange} placeholder="e.g., noreply@yourdomain.com" required />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="test-button" onClick={handleTestConnection} disabled={testing}>
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
          <button type="submit" className="save-button" disabled={saving}>
            <Save size={16} /> {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailConfiguration;